import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { effectiveLeverage, indicatorAutoLabel } from "@strat/format";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { StrategySpec } from "@strat/format";
import { useTablePrefs } from "@strat/ui/prefs";
import { ReportPdfButton, botReport } from "@strat/ui/export";

import { api } from "../../api";
import { PagedList } from "../../components/PagedList";
import { RefChip } from "../../components/RefChip";
import { CopyBotDialog, CopySettingsButton, useOpenInWorkbench } from "../../components/CopySettings";
import { EquityChart } from "../../components/EquityChart";
import { LiveChart } from "../../components/LiveChart";
import { MarketTag } from "../../components/MarketTag";
import { MarketTypeFilter } from "../../components/MarketTypeFilter";
import { matchesMarketType } from "../../components/marketType";
import type { MarketTypeChoice } from "../../components/marketType";
import { SortableTable } from "../../components/SortableTable";
import type { Column } from "../../components/SortableTable";
import { directionColumn } from "../../components/direction";
import { CardCollection, ViewToggle, useViewMode } from "../../components/ViewToggle";
import { SpecSummary } from "../../components/SpecSummary";
import {
  AuthorChip,
  CommentsPanel,
  Disclaimer,
  FollowButton,
  RatingPanel,
  Stars,
} from "../../components/social";
import { date, dd, money, pct, timeAgo, tone } from "../../format";
import { useSession } from "../../session";
import { Guard } from "../../shell/Guard";
import { Alert, Card, EmptyState, PageHead, Stat } from "../../shell/ui";
import { Icon } from "../../components/Icon";

export interface BotRow {
  id: string;
  name: string;
  strategy_id: string | null;
  symbol: string;
  timeframe: string;
  status: string;
  desired_state: "running" | "stopped";
  visibility: "public" | "private";
  equity: number | null;
  return_pct: number | null;
  max_drawdown_pct: number | null;
  trade_count: number;
  open_position: boolean;
  gap_count: number;
  follower_count: number;
  comment_count: number;
  rating_avg: number;
  rating_count: number;
  last_bar_at: number | null;
  started_at: string | null;
  created_at: string;
  spec_hash: string;
  engine_version: string;
  author_username: string;
  author_name: string;
  author_avatar: string;
  author_simulated: boolean;
  market_type?: string;
  /** From the bot's rules; 1 when unleveraged. */
  leverage?: number;
  /** Which way its entries open, from the bot's rules; long when they name none. */
  direction?: "long" | "short";
}

/** A bot's state as one word and a dot: running, warming up, stopped, crashed. */
export function BotState({ status, desired }: { status: string; desired: string }) {
  const label =
    desired === "stopped"
      ? "stopped"
      : status === "warming_up"
        ? "warming up"
        : status === "trading"
          ? "running"
          : status;
  const cls =
    desired === "stopped"
      ? ""
      : status === "crashed"
        ? "health-dot--fail"
        : status === "warming_up" || status === "starting"
          ? "health-dot--warn"
          : "";
  return (
    <span className="bot-state">
      <span
        className={`health-dot ${cls}`}
        aria-hidden="true"
        style={desired === "stopped" ? { background: "var(--text-faint)" } : undefined}
      />{" "}
      {label}
    </span>
  );
}

const botColumns = (withAuthor: boolean): Column<BotRow>[] => [
  {
    key: "name",
    label: "Bot",
    render: (b) => (
      <Link to={`/b/${b.id}`} onClick={(e) => e.stopPropagation()}>
        <strong>{b.name}</strong>
      </Link>
    ),
    sortValue: (b) => b.name,
    firstDirection: "asc",
  },
  ...(withAuthor
    ? [
        {
          key: "by",
          label: "Owner",
          render: (b: BotRow) => (
            <AuthorChip
              username={b.author_username}
              name={b.author_name}
              avatar={b.author_avatar}
              simulated={b.author_simulated}
            />
          ),
          sortValue: (b: BotRow) => b.author_name,
          firstDirection: "asc" as const,
        },
      ]
    : []),
  {
    key: "market",
    label: "Market",
    render: (b) => (
      <span className="mono small">
        {b.symbol} {b.timeframe} <MarketTag marketType={b.market_type} leverage={b.leverage} />
      </span>
    ),
    sortValue: (b) => `${b.symbol} ${b.timeframe}`,
    firstDirection: "asc",
  },
  directionColumn<BotRow>(),
  {
    key: "state",
    label: "State",
    render: (b) => <BotState status={b.status} desired={b.desired_state} />,
    sortValue: (b) => (b.desired_state === "stopped" ? "zz" : b.status),
    firstDirection: "asc",
  },
  {
    key: "ret",
    label: "Return",
    align: "right",
    render: (b) => <span className={tone(b.return_pct)}>{pct(b.return_pct, 2)}</span>,
    sortValue: (b) => b.return_pct,
  },
  {
    key: "dd",
    label: "Max DD",
    align: "right",
    render: (b) => dd(b.max_drawdown_pct),
    sortValue: (b) => b.max_drawdown_pct,
    firstDirection: "asc",
  },
  {
    key: "equity",
    label: "Equity",
    align: "right",
    render: (b) => money(b.equity, 2),
    sortValue: (b) => b.equity,
  },
  {
    key: "trades",
    label: "Trades",
    align: "right",
    render: (b) => b.trade_count,
    sortValue: (b) => b.trade_count,
  },
  {
    key: "pos",
    label: "Position",
    render: (b) => (b.open_position ? "open" : "flat"),
    sortValue: (b) => (b.open_position ? 1 : 0),
  },
  {
    key: "followers",
    label: "Followers",
    align: "right",
    render: (b) => b.follower_count,
    sortValue: (b) => b.follower_count,
  },
  {
    key: "age",
    label: "Running since",
    align: "right",
    render: (b) => timeAgo(b.started_at ?? b.created_at),
    sortValue: (b) => new Date(b.started_at ?? b.created_at).getTime(),
    firstDirection: "asc",
  },
];

interface LeaderRow {
  id: string;
  name: string;
  symbol: string;
  timeframe: string;
  status: string;
  author_username: string;
  author_name: string;
  author_avatar: string;
  author_simulated: boolean;
  trade_count: number;
  max_drawdown_pct: number | null;
  window_return_pct: number | null;
  window_trades: number;
  win_rate_pct: number | null;
  age_hours: number;
  follower_count: number;
  rating_avg: number;
}

type Period = "7d" | "30d" | "all";

/** A bot with its standing over the chosen period; null where it has no closed trade in it. */
interface RankedBot extends BotRow {
  rank: number | null;
  window_return_pct: number | null;
  window_trades: number;
  win_rate_pct: number | null;
}

const PERIODS: ReadonlyArray<{ id: Period; label: string }> = [
  { id: "7d", label: "Last 7d" },
  { id: "30d", label: "Last 30d" },
  { id: "all", label: "All time" },
];
const MIN_TRADES = [0, 1, 5, 20] as const;
/** Explore → Bots with nothing saved: running bots, all time, every market. */
const DEFAULT_EXPLORE = {
  state: "running",
  period: "all",
  timeframe: "any",
  symbol: "any",
  marketType: "all",
  minTrades: 0,
} as const;

/**
 * Every public paper bot, and how it is doing: one page for what used to be Live bots and the
 * Leaderboard. Pick a period and the return, trades and win rate are over it; narrow by state,
 * timeframe, market, owner kind, a minimum number of trades or a name; sort any column.
 * A bot is ranked only if it closed a trade in the period, so the rank is never from nothing.
 */
export function BotsExplorePage() {
  const navigate = useNavigate();
  // The filters open as this person last saved them ("Save as default"), else wide open.
  const { table: saved, set: saveTable } = useTablePrefs("explore-bots");
  const start = saved.filters ?? {};
  const [state, setState] = useState<"all" | "running">(start.state === "all" ? "all" : "running");
  const [period, setPeriod] = useState<Period>(
    PERIODS.some((p) => p.id === start.period) ? (start.period as Period) : "all",
  );
  const { user: me } = useSession();
  const isAdmin = me?.role === "admin";
  const [realPick, setReal] = useState(false);
  const real = isAdmin && realPick;
  const [timeframe, setTimeframe] = useState(typeof start.timeframe === "string" ? start.timeframe : "any");
  const [symbol, setSymbol] = useState(typeof start.symbol === "string" ? start.symbol : "any");
  const [marketType, setMarketType] = useState<MarketTypeChoice>(
    typeof start.marketType === "string" ? (start.marketType as MarketTypeChoice) : "all",
  );
  const [minTrades, setMinTrades] = useState<number>(
    typeof start.minTrades === "number" ? start.minTrades : 0,
  );
  const [search, setSearch] = useState("");
  const current = { state, period, timeframe, symbol, marketType, minTrades };
  const isDefault = Object.entries(current).every(
    ([k, v]) => (start[k] ?? DEFAULT_EXPLORE[k as never]) === v,
  );
  const [view, setView] = useViewMode("bots", "details");

  const bots = useQuery({
    queryKey: ["explore-bots", state, real],
    queryFn: () => api<{ items: BotRow[] }>(`/hub/explore/bots?state=${state}${real ? "&real=1" : ""}`),
    refetchInterval: 15_000,
  });
  const board = useQuery({
    queryKey: ["leaderboard", period, real],
    queryFn: () => api<{ items: LeaderRow[] }>(`/hub/leaderboard?window=${period}${real ? "&real=1" : ""}`),
    refetchInterval: 60_000,
  });

  const all: RankedBot[] = useMemo(() => {
    const standing = new Map((board.data?.items ?? []).map((r) => [r.id, r]));
    const merged = (bots.data?.items ?? []).map((b) => {
      const r = standing.get(b.id);
      return {
        ...b,
        rank: null as number | null,
        window_return_pct: r?.window_return_pct ?? null,
        window_trades: r?.window_trades ?? 0,
        win_rate_pct: r?.win_rate_pct ?? null,
      };
    });
    // The rank is by return over the period, among the bots that have one.
    [...merged]
      .filter((b) => b.window_return_pct !== null)
      .sort((a, b) => b.window_return_pct! - a.window_return_pct!)
      .forEach((b, i) => {
        b.rank = i + 1;
      });
    return merged;
  }, [bots.data, board.data]);

  const timeframes = useMemo(() => [...new Set(all.map((b) => b.timeframe))], [all]);
  const symbols = useMemo(() => [...new Set(all.map((b) => b.symbol))].sort(), [all]);
  const rows = all.filter(
    (b) =>
      (timeframe === "any" || b.timeframe === timeframe) &&
      (symbol === "any" || b.symbol === symbol) &&
      matchesMarketType(marketType, b.market_type) &&
      b.window_trades >= minTrades &&
      (!search.trim() || b.name.toLowerCase().includes(search.trim().toLowerCase())),
  );
  const filtered =
    timeframe !== "any" ||
    symbol !== "any" ||
    marketType !== "all" ||
    minTrades > 0 ||
    search.trim() !== "" ||
    real;

  const rankCol: Column<RankedBot> = {
    key: "rank",
    label: "#",
    align: "right",
    render: (b: RankedBot) => b.rank ?? "—",
    sortValue: (b: RankedBot) => b.rank,
    firstDirection: "asc",
  };
  const columns: Column<RankedBot>[] = [
    rankCol,
    ...botColumns(true).filter((c) => !["ret", "trades", "equity", "age"].includes(c.key as string)),
  ].map((c) => c as Column<RankedBot>);
  // Period-aware columns go after the identity ones, then the rest of the bot's standing.
  const identity = ["rank", "name", "by", "market", "direction", "state"];
  const nameCols = columns.filter((c) => identity.includes(c.key as string));
  const restCols = columns.filter((c) => !identity.includes(c.key as string));
  const periodCols: Column<RankedBot>[] = [
    {
      key: "ret",
      label: "Return",
      align: "right",
      render: (b) => <span className={tone(b.window_return_pct)}>{pct(b.window_return_pct, 2)}</span>,
      sortValue: (b) => b.window_return_pct,
    },
    {
      key: "trades",
      label: "Trades",
      align: "right",
      render: (b) => b.window_trades,
      sortValue: (b) => b.window_trades,
    },
    {
      key: "win",
      label: "Win rate",
      align: "right",
      render: (b) => pct(b.win_rate_pct, 0, false),
      sortValue: (b) => b.win_rate_pct,
    },
    {
      key: "equity",
      label: "Equity",
      align: "right",
      render: (b) => money(b.equity, 2),
      sortValue: (b) => b.equity,
    },
    {
      key: "age",
      label: "Running since",
      align: "right",
      render: (b) => timeAgo(b.started_at ?? b.created_at),
      sortValue: (b) => new Date(b.started_at ?? b.created_at).getTime(),
      firstDirection: "asc",
    },
  ];

  return (
    <div className="stack">
      <PageHead title="Bots">
        <div className="row">
          <div className="segmented" role="group" aria-label="Which bots">
            {(["running", "all"] as const).map((st) => (
              <button
                key={st}
                type="button"
                className={state === st ? "is-active" : undefined}
                onClick={() => setState(st)}
              >
                {st === "running" ? "Running" : "All"}
              </button>
            ))}
          </div>
          <div className="segmented" role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={period === p.id ? "is-active" : undefined}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <ViewToggle value={view} onChange={setView} />
        </div>
      </PageHead>
      <p className="muted">
        Public paper bots running on StrategyHive's servers against live prices, ranked by return over the
        period and always shown next to their drawdown. Every trade is observed by the hub and chained into a
        ledger nobody can edit. A bot is ranked only if it closed a trade in the period.
      </p>
      <div className="row" role="search" aria-label="Filter bots">
        <MarketTypeFilter value={marketType} onChange={setMarketType} />
        <input
          type="search"
          placeholder="Search by name"
          aria-label="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select aria-label="Timeframe" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
          <option value="any">Any timeframe</option>
          {timeframes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select aria-label="Market" value={symbol} onChange={(e) => setSymbol(e.target.value)}>
          <option value="any">Any market</option>
          {symbols.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          aria-label="Minimum trades"
          value={minTrades}
          onChange={(e) => setMinTrades(Number(e.target.value))}
        >
          {MIN_TRADES.map((n) => (
            <option key={n} value={n}>
              {n === 0 ? "Any number of trades" : `${n}+ trades in the period`}
            </option>
          ))}
        </select>
        {isAdmin && (
          <label className="checkbox">
            <input type="checkbox" checked={real} onChange={(e) => setReal(e.target.checked)} /> Real members
            only
          </label>
        )}
        {filtered && (
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              setTimeframe("any");
              setSymbol("any");
              setMarketType("all");
              setMinTrades(0);
              setSearch("");
              setReal(false);
            }}
          >
            Clear filters
          </button>
        )}
        {!isDefault && (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            title="Open Explore → Bots with these filters next time (Settings → Tables)"
            onClick={() => saveTable({ filters: current })}
          >
            Save as default
          </button>
        )}
        <span className="muted small">
          {rows.length} of {all.length} bots
        </span>
      </div>
      {view !== "details" ? (
        rows.length === 0 && !bots.isPending ? (
          <Card>
            <EmptyState icon="◎" title={filtered ? "No bots match these filters" : "No public bots yet"}>
              {filtered ? "Clear a filter, or widen the period." : undefined}
            </EmptyState>
          </Card>
        ) : (
          <CardCollection mode={view} busy={bots.isFetching}>
            {[...rows]
              .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity))
              .map((b) => (
                <BotCard key={b.id} b={b} />
              ))}
          </CardCollection>
        )
      ) : (
        <Card flush>
          <SortableTable
            tableId="explore-bots"
            rows={rows}
            loading={bots.isPending}
            rowKey={(b) => b.id}
            columns={[...nameCols, ...periodCols.slice(0, 3), ...restCols, ...periodCols.slice(3)]}
            onRowClick={(b) => navigate(`/b/${b.id}`)}
            initialSort={{ key: "ret", direction: "desc" }}
            empty={
              <EmptyState icon="◎" title={filtered ? "No bots match these filters" : "No public bots yet"}>
                {filtered ? "Clear a filter, or widen the period." : undefined}
              </EmptyState>
            }
          />
        </Card>
      )}
      <Disclaimer />
    </div>
  );
}

/** A public bot as a card (grid and list views), ranked by its return over the chosen period. */
function BotCard({ b }: { b: RankedBot }) {
  return (
    <Link to={`/b/${b.id}`} className="card strategy-card">
      <div className="card__body">
        <div className="strategy-card__head">
          <h2>
            {b.rank !== null && <span className="muted">#{b.rank} </span>}
            {b.name}
          </h2>
          <span className="row" style={{ gap: "0.3rem" }}>
            <span className="badge mono">
              {b.symbol} {b.timeframe}
            </span>
            <MarketTag marketType={b.market_type} leverage={b.leverage} />
          </span>
        </div>
        <AuthorChip
          username={b.author_username}
          name={b.author_name}
          avatar={b.author_avatar}
          simulated={b.author_simulated}
          suffix={<BotState status={b.status} desired={b.desired_state} />}
        />
        <div className="strategy-card__stats collection-numbers">
          <span>
            <span className="stat__label">Return</span>
            <strong className={tone(b.window_return_pct)}>{pct(b.window_return_pct, 2)}</strong>
          </span>
          <span>
            <span className="stat__label">Max DD</span>
            <strong>{dd(b.max_drawdown_pct)}</strong>
          </span>
          <span>
            <span className="stat__label">Trades</span>
            <strong>{b.window_trades}</strong>
          </span>
        </div>
        <div className="strategy-card__foot collection-aside">
          <span className="muted small">
            {b.open_position ? "position open" : "flat"} · equity {money(b.equity, 2)}
          </span>
          <span className="muted small" title="followers">
            👁 {b.follower_count}
          </span>
        </div>
      </div>
    </Link>
  );
}

function MyBotsInner() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["my-bots"],
    queryFn: () => api<{ bots: BotRow[] }>("/hub/me/space").then((r) => r.bots),
    refetchInterval: 10_000,
  });
  const quota = useQuery({
    queryKey: ["my-limits"],
    queryFn: () => api<{ limits: { bots: number } | null }>("/me/limits"),
  });
  const botLimit = quota.data?.limits?.bots;
  const limit = botLimit == null || botLimit < 0 ? null : botLimit;
  const [marketType, setMarketType] = useState<MarketTypeChoice>("all");
  const followed = useQuery({
    queryKey: ["followed-bots"],
    queryFn: () => api<{ items: BotRow[] }>("/hub/me/followed-bots").then((r) => r.items),
    refetchInterval: 30_000,
  });
  const mine = ((q.data ?? []) as BotRow[]).filter((b) => matchesMarketType(marketType, b.market_type));
  const following = (followed.data ?? []).filter((b) => matchesMarketType(marketType, b.market_type));
  return (
    <div className="stack">
      <PageHead title={limit === 1 ? "My bot" : "My bots"}>
        <div className="row">
          {(q.data?.length ?? 0) + (followed.data?.length ?? 0) > 1 && (
            <MarketTypeFilter value={marketType} onChange={setMarketType} />
          )}
          <span className="muted">{limit ? `${q.data?.length ?? 0} of ${limit}` : ""}</span>
        </div>
      </PageHead>
      <p className="muted">
        To start a bot, open one of your strategies (or any public one) and press{" "}
        <strong>Run as paper bot</strong>.
      </p>
      <Card flush>
        <SortableTable
          tableId="my-bots"
          rows={mine}
          rowKey={(b) => b.id}
          onRowClick={(b) => navigate(`/b/${b.id}`)}
          columns={botColumns(false).filter((c) => c.key !== "followers")}
          initialSort={{ key: "age", direction: "desc" }}
          empty={
            <EmptyState
              icon="◎"
              title="No bots yet"
              action={
                <Link className="btn btn--primary" to="/explore">
                  Find a strategy
                </Link>
              }
            >
              A paper bot runs a strategy live, with paper money, around the clock.
            </EmptyState>
          }
        />
      </Card>
      <Card title="Bots you follow" flush>
        <SortableTable
          tableId="followed-bots"
          rows={following}
          loading={followed.isPending}
          rowKey={(b) => b.id}
          onRowClick={(b) => navigate(`/b/${b.id}`)}
          columns={botColumns(true)}
          initialSort={{ key: "ret", direction: "desc" }}
          empty={
            <EmptyState
              icon="◇"
              title={followed.data?.length ? "None in this market" : "Not following a bot yet"}
              action={
                followed.data?.length ? undefined : (
                  <Link className="btn btn--ghost" to="/explore/bots">
                    Explore bots
                  </Link>
                )
              }
            >
              {followed.data?.length
                ? "Switch the market filter to All markets to see them."
                : "Press Follow on any public bot to keep it here, next to your own, and hear about its trades."}
            </EmptyState>
          }
        />
      </Card>
    </div>
  );
}

export function MyBotsPage() {
  return (
    <Guard role="starter">
      <MyBotsInner />
    </Guard>
  );
}

interface BotResponse {
  bot: BotRow & {
    spec: StrategySpec;
    market_type?: string;
    deploy: { startingCash: number };
    summary: {
      tradingState: string;
      barsSeen: number;
      startupBars: number;
      position: null | { direction: string; entryPrice: number; entryTime: number; size: number };
    } | null;
    stale: boolean;
  };
  equity: Array<[number, number]>;
  trades: Array<{
    seq: number;
    side: string;
    entry_time: number;
    exit_time: number;
    entry_price: number;
    exit_price: number;
    pnl: number;
    pnl_pct: number;
    reason: string;
  }>;
  gaps: Array<{ from_ts: number; to_ts: number; cause: string; bars_missed: number }>;
  strategy: { id: string; name: string } | null;
  strategyRef: {
    namespace: string;
    slug: string;
    version: number | null;
    digest: string;
    ref: string;
  } | null;
  events: Array<{ level: string; source: string; message: string; created_at: string }>;
  isOwner: boolean;
  /** rank null: in the league, but no closed trade this month yet. */
  league: { season_id: string; title: string; rank: number | null; of: number; score: number } | null;
}

interface Ledger {
  specHash: string;
  engineVersion: string;
  observedSince: string;
  entries: Array<{ seq: number; hash: string }>;
  intact: boolean;
  brokenAt: number | null;
}

export function BotPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useSession();
  const openInWorkbench = useOpenInWorkbench();
  const [copying, setCopying] = useState(false);
  const q = useQuery({
    queryKey: ["hub-bot", id],
    queryFn: () => api<BotResponse>(`/hub/bots/${encodeURIComponent(id)}`),
    refetchInterval: 15_000,
    retry: false,
  });
  const ledger = useQuery({
    queryKey: ["ledger", id],
    queryFn: () => api<Ledger>(`/hub/bots/${encodeURIComponent(id)}/ledger`),
    enabled: !!q.data,
  });
  const verify = useMutation({
    mutationFn: () =>
      api<{
        identical: boolean;
        liveTrades: number;
        replayTrades: number;
        matching: number;
        caveats: { detail: string }[];
      }>(`/hub/bots/${id}/verify`, { method: "POST", body: {} }),
  });
  const act = useMutation({
    mutationFn: (a: "start" | "stop" | "delete" | "close_all") =>
      a === "delete"
        ? api(`/hub/bots/${id}`, { method: "DELETE" })
        : a === "close_all"
          ? api(`/hub/bots/${id}/command`, { method: "POST", body: { command: "close_all" } })
          : api(`/hub/bots/${id}/${a}`, { method: "POST", body: {} }),
    onSuccess: (_r, a) => {
      if (a === "delete") navigate("/bots");
      else void qc.invalidateQueries({ queryKey: ["hub-bot", id] });
    },
  });
  if (q.isLoading) return null;
  if (!q.data)
    return (
      <Card>
        <EmptyState icon="◎" title="Bot not found">
          It may be private, or it was deleted.
        </EmptyState>
      </Card>
    );
  const { bot: b } = q.data;
  const pos = b.summary?.position;
  const inv = [["hub-bot", id]];
  const intraday = ["1m", "3m", "5m", "15m", "30m", "1h"].includes(b.timeframe);
  // "Backtest these settings" covers the last six months: long enough to mean
  // something, and the bot's own live record is right here to compare with.
  const backtestFrom = Date.now() - 182 * 86_400_000;
  const settings = {
    strategy: b.spec,
    symbol: b.symbol,
    timeframe: b.timeframe,
    marketType: b.market_type ?? "spot",
    from: backtestFrom,
    to: null,
    initialCapital: b.deploy.startingCash,
  };

  return (
    <div className="stack detail">
      <header className="detail__head">
        <div>
          <p className="muted small">
            Paper bot · {b.symbol} {b.timeframe}
            {b.market_type === "futures" ? ` · futures ${effectiveLeverage(b.spec.risk)}×` : ""} ·{" "}
            {b.visibility}
          </p>
          <h1 className="page__title">{b.name}</h1>
          <p className="detail__meta">
            <AuthorChip
              username={b.author_username}
              name={b.author_name}
              avatar={b.author_avatar}
              simulated={b.author_simulated}
            />
            <BotState status={b.status} desired={b.desired_state} />
            {q.data.league &&
              (q.data.league.rank === null ? (
                <Link
                  className="badge league-rank league-rank--unranked"
                  to={`/league/${q.data.league.season_id}`}
                  title="In this month's league; ranked after its first closed trade this month"
                >
                  <Icon name="league" /> Unranked in {q.data.league.title}
                </Link>
              ) : (
                <Link
                  className="badge league-rank"
                  to={`/league/${q.data.league.season_id}`}
                  title={`Ranked by return ÷ max drawdown this month (score ${q.data.league.score.toFixed(2)})`}
                >
                  <Icon name="league" /> #{q.data.league.rank} of {q.data.league.of} in {q.data.league.title}
                </Link>
              ))}
            {b.stale && <span className="badge badge--warn">behind its feed</span>}
            <Stars value={b.rating_avg} count={b.rating_count} />
          </p>
          {q.data.strategy && (
            <p className="small">
              Runs <Link to={`/s/${q.data.strategy.id}`}>{q.data.strategy.name}</Link>
              {q.data.strategyRef && (
                <>
                  {" "}
                  <RefChip
                    namespace={q.data.strategyRef.namespace}
                    slug={q.data.strategyRef.slug}
                    version={q.data.strategyRef.version}
                    digest={q.data.strategyRef.digest}
                  />
                </>
              )}
            </p>
          )}
        </div>
        <div className="row detail__actions">
          {q.data.isOwner && (
            <>
              {b.desired_state === "running" ? (
                <button
                  type="button"
                  className="btn"
                  disabled={act.isPending}
                  onClick={() => act.mutate("stop")}
                >
                  ■ Stop
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={act.isPending}
                  onClick={() => act.mutate("start")}
                >
                  <Icon name="play" /> Start
                </button>
              )}
              {pos && b.desired_state === "running" && (
                <button
                  type="button"
                  className="btn"
                  disabled={act.isPending}
                  onClick={() => window.confirm("Close the open position now?") && act.mutate("close_all")}
                >
                  Close position
                </button>
              )}
              <button
                type="button"
                className="btn btn--danger"
                disabled={act.isPending}
                onClick={() =>
                  window.confirm("Delete this bot and its record? This can't be undone.") &&
                  act.mutate("delete")
                }
              >
                Delete
              </button>
            </>
          )}
          <ReportPdfButton
            size="md"
            build={() =>
              botReport({
                name: b.name,
                strategyName: q.data.strategy?.name ?? b.spec.name,
                symbol: b.symbol,
                timeframe: b.timeframe,
                marketType: b.market_type ?? "spot",
                status: b.status,
                startingCash: b.deploy.startingCash,
                startedAt: b.started_at ? Date.parse(b.started_at) : null,
                lastBarAt: b.last_bar_at,
                equity: q.data.equity,
                currentEquity: b.equity,
                maxDrawdownPct: b.max_drawdown_pct,
                totalTradeCount: b.trade_count,
                trades: q.data.trades.map((t) => ({
                  seq: t.seq,
                  direction: t.side,
                  entryTime: Number(t.entry_time),
                  exitTime: Number(t.exit_time),
                  entryPrice: t.entry_price,
                  exitPrice: t.exit_price,
                  pnl: t.pnl,
                  pnlPct: t.pnl_pct,
                  exitReason: t.reason,
                })),
                position: pos
                  ? `${pos.direction} ${pos.size.toPrecision(4)} @ ${money(pos.entryPrice, 2)}`
                  : null,
                gaps: q.data.gaps.map((g) => ({ from: g.from_ts, bars: g.bars_missed, cause: g.cause })),
                spec: b.spec,
                extraMeta: [
                  ["Author", `@${b.author_username}`],
                  ["Visibility", b.visibility],
                ],
              })
            }
          />
          <FollowButton type="bot" id={b.id} invalidate={inv} />
        </div>
      </header>
      <div className="settings-bar">
        <div>
          <strong>Use these settings</strong>
          <p className="muted small action-hint">
            {b.symbol} {b.timeframe} · {money(b.deploy.startingCash)} paper cash ·{" "}
            {b.spec.indicators
              .map((d) => d.label?.trim() || indicatorAutoLabel(d, b.spec.indicators))
              .join(" + ") || "price rules"}
            {b.spec.risk.stopLossPct ? ` · stop ${b.spec.risk.stopLossPct}%` : ""}
            {effectiveLeverage(b.spec.risk) > 1 ? ` · ${effectiveLeverage(b.spec.risk)}× leverage` : ""}
          </p>
        </div>
        <div className="row">
          {user && !q.data.isOwner && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setCopying(true)}
              title="Start a paper bot of your own with the same rules"
            >
              <Icon name="copy" /> Run a copy
            </button>
          )}
          {user ? (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() =>
                openInWorkbench({
                  ...settings,
                  spec: b.spec,
                  marketType: b.market_type === "futures" ? "futures" : "spot",
                })
              }
              title="Open the Workbench on these rules and this market, over the last six months"
            >
              <Icon name="workbench" /> Backtest these settings
            </button>
          ) : (
            <Link className="btn btn--sm" to={`/signin?next=${encodeURIComponent(`/b/${b.id}`)}`}>
              Sign in to copy or backtest
            </Link>
          )}
          <CopySettingsButton settings={settings} />
        </div>
      </div>
      {copying && (
        <CopyBotDialog
          bot={{
            id: b.id,
            name: b.name,
            symbol: b.symbol,
            timeframe: b.timeframe,
            startingCash: b.deploy.startingCash,
            isOwner: q.data.isOwner,
            marketType: b.market_type,
            risk: b.spec.risk,
            direction: b.spec.direction,
          }}
          onClose={() => setCopying(false)}
        />
      )}
      {act.error && <Alert tone="error">{act.error.message}</Alert>}

      <div className="detail__grid">
        <div className="stack">
          <Card title="Live chart">
            <LiveChart
              symbol={b.symbol}
              timeframe={b.timeframe}
              marketType={b.market_type === "futures" ? "futures" : "spot"}
              indicators={b.spec.indicators}
              trades={q.data.trades.map((t) => ({
                entryTime: Number(t.entry_time),
                entryPrice: t.entry_price,
                exitTime: Number(t.exit_time),
                exitPrice: t.exit_price,
                direction: t.side,
                pnlPct: t.pnl_pct,
                exitReason: t.reason,
              }))}
              position={
                pos
                  ? {
                      direction: pos.direction === "short" ? "short" : "long",
                      entryTime: pos.entryTime,
                      entryPrice: pos.entryPrice,
                    }
                  : null
              }
              stopLossPct={b.spec.risk.stopLossPct}
              since={b.started_at ? new Date(b.started_at).getTime() : null}
              running={b.desired_state === "running"}
            />
          </Card>
          <Card title="Live equity">
            <div className="stat-grid stat-grid--pairs">
              <Stat
                label="Return"
                value={<span className={tone(b.return_pct)}>{pct(b.return_pct, 2)}</span>}
              />
              <Stat label="Equity" value={money(b.equity, 2)} />
              <Stat label="Max drawdown" value={dd(b.max_drawdown_pct)} />
              <Stat label="Closed trades" value={b.trade_count} />
              <Stat
                label="Position"
                value={pos ? `${pos.direction} @ ${money(pos.entryPrice, 2)}` : "flat"}
              />
              <Stat
                label="Last bar"
                value={
                  b.last_bar_at
                    ? timeAgo(b.last_bar_at)
                    : b.summary?.tradingState === "warming_up"
                      ? `warming up ${b.summary.barsSeen}/${b.summary.startupBars}`
                      : "—"
                }
              />
            </div>
            <EquityChart
              points={q.data.equity}
              baseline={b.deploy.startingCash}
              label="Bot equity"
              intraday={intraday}
            />
          </Card>
          <Card title={`Trades (${q.data.trades.length})`} flush>
            <SortableTable
              exportName={["trades", b.name, b.symbol, b.timeframe]}
              tableId="bot-trades"
              rows={q.data.trades}
              rowKey={(t) => String(t.seq)}
              initialSort={{ key: "seq", direction: "desc" }}
              empty={<p className="card__body muted">No closed trades yet.</p>}
              columns={[
                { key: "seq", label: "#", align: "right", render: (t) => t.seq, sortValue: (t) => t.seq },
                {
                  key: "side",
                  label: "Side",
                  render: (t) => t.side,
                  sortValue: (t) => t.side,
                  firstDirection: "asc",
                },
                {
                  key: "entry",
                  label: "Opened",
                  render: (t) => date(t.entry_time, true),
                  sortValue: (t) => Number(t.entry_time),
                },
                {
                  key: "exit",
                  label: "Closed",
                  render: (t) => date(t.exit_time, true),
                  sortValue: (t) => Number(t.exit_time),
                },
                {
                  key: "in",
                  label: "Entry",
                  align: "right",
                  render: (t) => money(t.entry_price, 2),
                  sortValue: (t) => t.entry_price,
                  exportValue: (t) => t.entry_price,
                },
                {
                  key: "out",
                  label: "Exit",
                  align: "right",
                  render: (t) => money(t.exit_price, 2),
                  sortValue: (t) => t.exit_price,
                  exportValue: (t) => t.exit_price,
                },
                {
                  key: "pnl",
                  label: "P&L",
                  align: "right",
                  render: (t) => <span className={tone(t.pnl)}>{money(t.pnl, 2)}</span>,
                  sortValue: (t) => t.pnl,
                  exportValue: (t) => t.pnl,
                },
                {
                  key: "pct",
                  label: "P&L %",
                  align: "right",
                  render: (t) => <span className={tone(t.pnl_pct)}>{pct(t.pnl_pct * 100, 2)}</span>,
                  sortValue: (t) => t.pnl_pct,
                  exportValue: (t) => t.pnl_pct * 100,
                },
                {
                  key: "why",
                  label: "Exit reason",
                  render: (t) => t.reason?.replace(/_/g, " "),
                  sortValue: (t) => t.reason,
                  firstDirection: "asc",
                },
              ]}
            />
          </Card>
          <Card>
            <CommentsPanel type="bot" id={b.id} invalidate={inv} />
          </Card>
        </div>
        <div className="stack">
          <Card title="Observed by StrategyHive">
            {ledger.data ? (
              <div className="stack small">
                <p>
                  <span className={ledger.data.intact ? "up" : "down"}>
                    {ledger.data.intact
                      ? "✓ Ledger intact"
                      : `✗ Ledger broken at entry ${ledger.data.brokenAt}`}
                  </span>{" "}
                  — {ledger.data.entries.length} trades chained since {date(ledger.data.observedSince, true)}.
                </p>
                <p className="muted">
                  Each trade is hashed together with the one before it, the strategy's hash (
                  <code title={ledger.data.specHash}>{ledger.data.specHash.slice(0, 12)}…</code>) and engine{" "}
                  {ledger.data.engineVersion}. Changing any past trade would break every hash after it.
                </p>
              </div>
            ) : (
              <p className="muted">Loading…</p>
            )}
            <div className="row" style={{ marginTop: "0.8rem" }}>
              <button
                type="button"
                className="btn btn--sm"
                disabled={verify.isPending}
                onClick={() => verify.mutate()}
              >
                {verify.isPending ? "Replaying…" : "↻ Verify against a backtest"}
              </button>
            </div>
            {verify.error && <Alert tone="error">{verify.error.message}</Alert>}
            {verify.data && (
              <div className={`verdict ${verify.data.identical ? "verdict--good" : "verdict--warn"}`}>
                <span className="verdict__mark">{verify.data.identical ? "✓" : "!"}</span>
                <div>
                  <strong>
                    {verify.data.identical
                      ? "The live trades match a backtest of the same bars."
                      : `${verify.data.matching} of ${verify.data.liveTrades} live trades match the replay.`}
                  </strong>
                  {verify.data.caveats.map((c, i) => (
                    <p key={i} className="small muted">
                      {c.detail}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </Card>
          <Card title="Ratings">
            <RatingPanel type="bot" id={b.id} isOwner={q.data.isOwner} invalidate={inv} />
          </Card>
          <Card title="Strategy">
            <SpecSummary spec={b.spec} />
          </Card>
          <Card title="Gaps in the record">
            {q.data.gaps.length === 0 ? (
              <p className="muted small">None — the bot has seen every bar since it started.</p>
            ) : (
              <PagedList
                className="versions"
                items={q.data.gaps}
                pageSize={10}
                render={(g) => (
                  <li key={g.from_ts} className="small">
                    {g.bars_missed} bars (
                    {g.cause === "offline" ? "bot was offline — replayed on resume" : "feed lost them"}) ·{" "}
                    {date(g.from_ts, true)}
                  </li>
                )}
              />
            )}
          </Card>
          {q.data.isOwner && q.data.events.length > 0 && (
            <Card title="Log" flush>
              <PagedList
                className="log-tail"
                items={q.data.events}
                render={(e, i) => (
                  <li key={i} className={`log-line${e.level !== "info" ? ` log-line--${e.level}` : ""}`}>
                    <span className="muted">{new Date(e.created_at).toLocaleTimeString()}</span> {e.message}
                  </li>
                )}
              />
            </Card>
          )}
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}
